import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Grid,
  Button,
  TextField,
  Typography,
  CircularProgress
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { CloseIcon, QueryBuilder } from 'components';
import { getSegmentedList, resetSnackbar } from 'store/lists/actions';
import UserMessageSnackbar from 'views/UserMessaging/customModalContent/userMessageSnackbar';
import useLists from './useLists';
import sortBy from 'lodash.sortby';
import { ProductType } from 'constants.js';
import { getEventMetaData } from 'store/event/actions';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  header: {
    display: 'flex',
    width: '100%'
  },
  title: {
    width: '95%'
  },
  cardActions: {
    justifyContent: 'space-between'
  },
  templateButtonError: {
    paddingLeft: '19px',
    color: theme.palette.error.main
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    margin: '5%'
  },
  textInput: {
    marginBottom: '16px'
  }
}));

const SegmentedListSettings = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const lists = useLists();
  const { listId, segmentedListId } = useParams();

  const {
    updateCurrentErrorValidationSegment,
    shouldShowError,
    getErrorMessage,
    setSegmentedListName,
    onClickSaveSegmentedList,
    handleChange,
    onBlur,
    thisSegmentedList
  } = lists;

  const SCHEMA_IDENTIFIER = 'ADM.ListManagement.Csv';
  const PRODUCT_NAME = 'ListManagement';

  const isLoading = useSelector(store => store.lists.segmentedList.getting);
  const showSnackbar = useSelector(store => store.lists.showSnackbar);
  const snackbarMessage = useSelector(store => store.lists.snackbarMessage);
  const currentSegmentedList = useSelector(
    store => store.lists.segmentedList.currentList
  );
  const eventMetaData = useSelector(store => store.events.eventMetaData.data);
  const isSaveSegmentedListSucceeded = useSelector(
    store => store.lists.segmentedList.saved
  );

  const [snackbarInfo, setSnackBarInfo] = useState({ show: false });
  const [listHeaders, setListHeaders] = useState([]);

  useEffect(() => {
    dispatch(resetSnackbar());
    dispatch(
      getEventMetaData({
        schemaIdentifier: SCHEMA_IDENTIFIER,
        productName: PRODUCT_NAME,
        workflow: 'template',
        productType: ProductType.List
      })
    );
    if (segmentedListId) {
      dispatch(getSegmentedList(segmentedListId));
    }
    updateCurrentErrorValidationSegment('createSegmentedList');
  }, []);

  useEffect(() => {
    if (
      isSaveSegmentedListSucceeded &&
      currentSegmentedList?.id &&
      !segmentedListId
    ) {
      history.push(
        `/lists/${listId}/segmented-list/${currentSegmentedList.id}/settings`
      );
    }
  }, [isSaveSegmentedListSucceeded]);

  useEffect(() => {
    if (showSnackbar) {
      if (isSaveSegmentedListSucceeded) {
        setSnackBarInfo({
          show: true,
          message: 'Saved successfully.',
          severity: 'success'
        });
      } else {
        setSnackBarInfo({
          show: true,
          message: snackbarMessage || 'Failed to save. Try again.',
          severity: 'error'
        });
      }
    } else {
      setSnackBarInfo({
        show: false,
        message: ''
      });
    }
  }, [showSnackbar]);

  const deCamelizer = str => {
    return str
      .replace(/([A-Z])/g, ' $1')
      .replace(/(\d+)/g, ' $1')
      .replace(/^./, str => str.toUpperCase());
  };

  const getProductName = data => {
    return (data.fieldFromSchema || data.schemaType === "EVENT")
      ? `Event Product - ${PRODUCT_NAME}`
      : ((data.fieldFromSchemaEnrichment || data.schemaType === "ENRICHMENT")
        ? `Enrichment Product - ${data.enrichmentProduct}`
        : data.schemaTypeDisplayName
      );
  };

  useEffect(() => {
    if (eventMetaData && eventMetaData.length > 0) {
      let hydratedEventMetaData = sortBy(
        eventMetaData.map(data => ({
          ...data,
          productName: getProductName(data),
          schemaConditionAttributes: data.schemaConditionAttributes.map(a => ({
            key: a.conditionAttribute,
            name:
              !a.displayName || a.displayName === ''
                ? deCamelizer(a.conditionAttribute)
                : a.displayName,
            dataType: a.conditionAttributeType,
            text: `{{${a.conditionAttribute}}}`,
            attributeFieldType: a.attributeFieldType,
            description: a.description,
            subAttribute: {
              ...a?.subAttribute,
              attributes:
                a?.subAttribute?.attributes &&
                a.subAttribute['attributes'].map(b => ({
                  key: b.conditionAttribute,
                  name:
                    !b.displayName || b.displayName === ''
                      ? deCamelizer(b.conditionAttribute)
                      : b.displayName,
                  dataType: b.conditionAttributeType,
                  text: `{{${b.conditionAttribute}}}`,
                  description: b.description
                }))
            },
            productName: getProductName(data)
          }))
        })),
        'name'
      );
      setListHeaders(hydratedEventMetaData);
    }
  }, [eventMetaData]);

  const closeCreateSegmentedList = () => {
    history.push(`/lists/${listId}/segmented-list`);
  };

  return (
    <div className={classes.root}>
      <Card>
        <form autoComplete="off">
          <div className={classes.header}>
            <CardHeader
              className={classes.title}
              subheader="* indicates a required field"
              title="Segmented List Settings"
              titleTypographyProps={{ variant: 'h3' }}
            />
            <CloseIcon
              style={{ paddingBottom: '16px' }}
              onClickClose={closeCreateSegmentedList}
            />
          </div>
          <Divider />
          <CardContent>
            {isLoading ? (
              <div className={classes.loader}>
                <CircularProgress />
              </div>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Grid item md={6} xs={12} className={classes.inputGrid}>
                    <TextField
                      fullWidth
                      className={classes.textInput}
                      label="Segmented list name"
                      margin="dense"
                      variant="outlined"
                      required
                      value={thisSegmentedList.name || ''}
                      onChange={event =>
                        setSegmentedListName(event.target.value)
                      }
                      onBlur={onBlur}
                      error={shouldShowError('name')}
                      helperText={getErrorMessage('name')}
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.inputGrid}>
                    <QueryBuilder
                      name="conditionExpression"
                      template={{
                        thisTemplate: {
                          eventMetaData: listHeaders
                        }
                      }}
                      onChange={event => handleChange(event.target.value)}
                      query={thisSegmentedList.segmentDefinition || {}}
                      required="true"
                    />
                    {(shouldShowError(
                      `segmentDefinition.subConditionExpressions`
                    ) ||
                      shouldShowError(`segmentDefinition.valid`)) && (
                      <Typography
                        className={classes.templateButtonError}
                        variant="caption"
                        display="block">
                        {shouldShowError(
                          `segmentDefinition.subConditionExpressions`
                        )
                          ? '*Please add at least one rule.'
                          : '*Please enter a complete valid expression.'}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}
          </CardContent>
          <>
            <Divider />
            <CardActions className={classes.cardActions}>
              <Button
                className={classes.backButton}
                color="secondary"
                variant="contained"
                onClick={closeCreateSegmentedList}>
                Back
              </Button>
              <Button
                color="primary"
                variant="contained"
                disabled={isLoading}
                onClick={() => onClickSaveSegmentedList()}>
                Save Segmented list
              </Button>
            </CardActions>
          </>
        </form>
      </Card>
      {showSnackbar && (
        <UserMessageSnackbar
          openSnackbar={snackbarInfo.show}
          message={snackbarInfo.message}
          severity={snackbarInfo.severity}
          onClose={() => dispatch(resetSnackbar())}
        />
      )}
    </div>
  );
};

export default SegmentedListSettings;
